import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate, useParams } from 'react-router-dom';
import Select from 'react-select';
import { Button, Card, CardBody, Chip, Spinner, Typography } from '@material-tailwind/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faArrowLeft,
    faDownload,
    faRotateRight,
    faSearch,
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../AuthContext';
import { rootUrl } from '../../../config/config';
import {
    buildCashbookStatementCsv,
    buildCashbookStatementRows,
    fetchAllCashbookEntries,
    formatMoney,
} from './cashbookStatementUtils';

const APP_FONT_FAMILY = "'Nunito', ui-sans-serif, system-ui, sans-serif";

const selectStyles = {
    control: (base, state) => ({
        ...base,
        minHeight: '44px',
        borderRadius: '0.75rem',
        borderColor: state.isFocused ? '#FF5733' : '#cbd5e1',
        boxShadow: 'none',
        fontFamily: APP_FONT_FAMILY,
        '&:hover': {
            borderColor: '#FF5733',
        },
    }),
    menu: (base) => ({
        ...base,
        zIndex: 9999,
        borderRadius: '0.75rem',
        overflow: 'hidden',
        fontFamily: APP_FONT_FAMILY,
    }),
    option: (base, state) => ({
        ...base,
        fontFamily: APP_FONT_FAMILY,
        backgroundColor: state.isFocused ? '#fff1eb' : '#ffffff',
        color: '#334155',
        cursor: 'pointer',
    }),
};

const entryTypeOptions = [
    { value: '', label: 'All entry types' },
    { value: 'INCOME', label: 'Income' },
    { value: 'EXPENSE', label: 'Expense' },
    { value: 'TRANSFER_IN', label: 'Transfer In' },
    { value: 'TRANSFER_OUT', label: 'Transfer Out' },
];

const reconciledOptions = [
    { value: '', label: 'All statuses' },
    { value: 'true', label: 'Reconciled' },
    { value: 'false', label: 'Pending' },
];

const formatDate = (value) => {
    if (!value) return '-';
    const datePart = String(value).slice(0, 10);
    const date = new Date(`${datePart}T00:00:00`);
    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleDateString('en-US', {
        month: 'long',
        day: '2-digit',
        year: 'numeric',
    });
};

const formatDateTime = (value) => {
    if (!value) return '-';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return String(value);

    return date.toLocaleString('en-KE', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
    });
};

const CashbookStatement = () => {
    const { accountId } = useParams();
    const navigate = useNavigate();
    const { token } = useAuth();
    const authToken = token || localStorage.getItem('authToken') || 'default-token';

    const [account, setAccount] = useState(null);
    const [entries, setEntries] = useState([]);
    const [loadingAccount, setLoadingAccount] = useState(true);
    const [loadingEntries, setLoadingEntries] = useState(true);
    const [error, setError] = useState('');
    const [filters, setFilters] = useState({
        date_from: '',
        date_to: '',
        entry_type: '',
        reconciled: '',
    });
    const [searchInput, setSearchInput] = useState('');
    const [searchText, setSearchText] = useState('');

    const headers = useMemo(() => ({ Authorization: `Bearer ${authToken}` }), [authToken]);

    const loadAccount = useCallback(async () => {
        if (!accountId) {
            setError('Missing cashbook account id.');
            setLoadingAccount(false);
            return;
        }

        setLoadingAccount(true);
        setError('');

        try {
            const response = await axios.get(`${rootUrl}/api/cashbook/accounts/${accountId}`, { headers });
            if (response.data?.success) {
                setAccount(response.data.data || null);
            } else {
                setAccount(null);
                setError(response.data?.message || 'Failed to load cashbook account.');
            }
        } catch (err) {
            setAccount(null);
            setError(err.response?.data?.message || 'Failed to load cashbook account.');
        } finally {
            setLoadingAccount(false);
        }
    }, [accountId, headers]);

    const loadEntries = useCallback(async () => {
        if (!accountId) {
            setEntries([]);
            setLoadingEntries(false);
            return;
        }

        setLoadingEntries(true);

        try {
            const rows = await fetchAllCashbookEntries({
                baseUrl: rootUrl,
                headers,
                accountId,
            });
            setEntries(rows);
        } catch (err) {
            setEntries([]);
            setError(err.response?.data?.message || err.message || 'Failed to load cashbook entries.');
        } finally {
            setLoadingEntries(false);
        }
    }, [accountId, headers]);

    useEffect(() => {
        loadAccount();
    }, [loadAccount]);

    useEffect(() => {
        loadEntries();
    }, [loadEntries]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setSearchText(searchInput.trim().toLowerCase());
        }, 250);

        return () => clearTimeout(timer);
    }, [searchInput]);

    const derivedRows = useMemo(() => {
        return buildCashbookStatementRows(entries, account?.opening_balance || 0);
    }, [account?.opening_balance, entries]);

    const openingBalance = Number(account?.opening_balance || 0);
    const totalDebits = useMemo(() => {
        return derivedRows.reduce((sum, row) => sum + Number(row.debit || 0), 0);
    }, [derivedRows]);
    const totalCredits = useMemo(() => {
        return derivedRows.reduce((sum, row) => sum + Number(row.credit || 0), 0);
    }, [derivedRows]);
    const closingBalance = derivedRows.length > 0
        ? (derivedRows[derivedRows.length - 1]?.running_balance ?? openingBalance)
        : openingBalance;

    const filteredRows = useMemo(() => {
        return derivedRows.filter((row) => {
            if (filters.entry_type && String(row.entry_type || '') !== filters.entry_type) {
                return false;
            }

            if (filters.reconciled !== '') {
                const reconciledValue = String(Boolean(row.reconciled));
                if (reconciledValue !== filters.reconciled) {
                    return false;
                }
            }

            if (filters.date_from && row.entry_date_value && row.entry_date_value < filters.date_from) {
                return false;
            }

            if (filters.date_to && row.entry_date_value && row.entry_date_value > filters.date_to) {
                return false;
            }

            if (searchText && !String(row.search_text || '').includes(searchText)) {
                return false;
            }

            return true;
        });
    }, [derivedRows, filters, searchText]);

    const dateRangeLabel = useMemo(() => {
        if (filters.date_from && filters.date_to) {
            return `${filters.date_from} to ${filters.date_to}`;
        }
        if (filters.date_from) {
            return `From ${filters.date_from}`;
        }
        if (filters.date_to) {
            return `Up to ${filters.date_to}`;
        }
        return 'All dates';
    }, [filters.date_from, filters.date_to]);

    const filteredTotalDebits = useMemo(() => {
        return filteredRows.reduce((sum, row) => sum + Number(row.debit || 0), 0);
    }, [filteredRows]);

    const filteredTotalCredits = useMemo(() => {
        return filteredRows.reduce((sum, row) => sum + Number(row.credit || 0), 0);
    }, [filteredRows]);

    const handleResetFilters = () => {
        setFilters({
            date_from: '',
            date_to: '',
            entry_type: '',
            reconciled: '',
        });
        setSearchInput('');
        setSearchText('');
    };

    const handleExport = () => {
        if (!account) return;

        const csv = buildCashbookStatementCsv({
            account,
            openingBalance,
            closingBalance,
            dateRangeLabel,
            rows: filteredRows,
        });

        const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
        const url = URL.createObjectURL(blob);
        const anchor = document.createElement('a');
        anchor.href = url;
        anchor.download = `cashbook-statement-${account.id}.csv`;
        anchor.click();
        URL.revokeObjectURL(url);
    };

    const loading = loadingAccount || loadingEntries;

    return (
        <div className="min-h-screen w-full min-w-0 overflow-x-hidden bg-gradient-to-br from-[#fff8f5] to-[#EDE4DA]">
            <div className="p-6 lg:p-8">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div>
                        <Button
                            type="button"
                            onClick={() => navigate('/cashbook/accounts')}
                            className="mb-4 inline-flex items-center gap-2 bg-slate-100 text-slate-700 shadow-none hover:bg-slate-200"
                        >
                            <FontAwesomeIcon icon={faArrowLeft} />
                            Back to Accounts
                        </Button>
                        <h1 className="text-4xl font-bold text-[#2a2e30] mb-2">Cashbook Statement</h1>
                        <div className="w-24 h-1 bg-[#FF5733] rounded-full mb-4"></div>
                        <p className="text-lg text-[#2a2e30] opacity-80">
                            Review a full statement ledger with filters and export only.
                        </p>
                    </div>

                    <div className="flex flex-wrap gap-2">
                        <Button
                            type="button"
                            onClick={() => {
                                loadAccount();
                                loadEntries();
                            }}
                            className="bg-slate-100 hover:bg-slate-200 text-slate-700 shadow-none inline-flex items-center gap-2"
                        >
                            <FontAwesomeIcon icon={faRotateRight} className={loading ? 'animate-spin' : ''} />
                            Refresh
                        </Button>
                        <Button
                            type="button"
                            onClick={handleExport}
                            disabled={!account || filteredRows.length === 0}
                            className="bg-[#FF5733] hover:bg-[#e64520] text-white shadow-none inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            <FontAwesomeIcon icon={faDownload} />
                            Export CSV
                        </Button>
                    </div>
                </div>
            </div>

            <div className="w-full min-w-0 px-3 sm:px-4 lg:px-6 pb-8 space-y-6">
                {error && (
                    <Card className="border border-red-200 bg-red-50 shadow-none rounded-xl">
                        <CardBody className="p-4 flex items-start gap-3">
                            <Typography className="text-sm text-red-700">{error}</Typography>
                        </CardBody>
                    </Card>
                )}

                <Card className="border border-[#eadfd8] shadow-sm rounded-xl bg-white">
                    <CardBody className="p-4 sm:p-5">
                        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                            <div>
                                <Typography className="text-base font-bold text-[#2a2e30]">
                                    {account?.account_name || 'Loading account...'}
                                </Typography>
                                <Typography className="text-sm text-slate-500">
                                    {account?.bank_name || 'Internal account'}
                                    {account?.bank_account_number ? ` • ${account.bank_account_number}` : ''}
                                </Typography>
                                <Typography className="text-xs text-slate-400 mt-1">
                                    {account?.account_type || 'Account type unavailable'} • Loaded {formatDateTime(account?.input_date || account?.edit_date)}
                                </Typography>
                            </div>

                            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 w-full lg:w-auto">
                                <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                                    <Typography className="text-xs uppercase tracking-wide text-slate-500">Opening</Typography>
                                    <Typography className="text-base font-bold text-[#2a2e30] tabular-nums">{formatMoney(openingBalance)}</Typography>
                                </div>
                                <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                                    <Typography className="text-xs uppercase tracking-wide text-slate-500">Debits</Typography>
                                    <Typography className="text-base font-bold text-red-600 tabular-nums">{formatMoney(totalDebits)}</Typography>
                                </div>
                                <div className="rounded-xl border border-slate-100 bg-slate-50 px-4 py-3">
                                    <Typography className="text-xs uppercase tracking-wide text-slate-500">Credits</Typography>
                                    <Typography className="text-base font-bold text-emerald-600 tabular-nums">{formatMoney(totalCredits)}</Typography>
                                </div>
                                <div className="rounded-xl border border-slate-100 bg-[#fff4ef] px-4 py-3">
                                    <Typography className="text-xs uppercase tracking-wide text-slate-500">Closing</Typography>
                                    <Typography className="text-base font-bold text-[#2a2e30] tabular-nums">{formatMoney(closingBalance)}</Typography>
                                </div>
                            </div>
                        </div>
                    </CardBody>
                </Card>

                <Card className="border border-[#eadfd8] shadow-sm rounded-xl bg-white">
                    <CardBody className="p-4 sm:p-5">
                        <div className="grid grid-cols-1 xl:grid-cols-5 gap-4">
                            <div className="min-w-0">
                                <Typography className="text-sm font-semibold text-slate-700 mb-1.5">Date From</Typography>
                                <input
                                    type="date"
                                    value={filters.date_from}
                                    onChange={(event) => setFilters((previous) => ({ ...previous, date_from: event.target.value }))}
                                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-1 focus:ring-[#FF5733] outline-none"
                                />
                            </div>
                            <div className="min-w-0">
                                <Typography className="text-sm font-semibold text-slate-700 mb-1.5">Date To</Typography>
                                <input
                                    type="date"
                                    value={filters.date_to}
                                    onChange={(event) => setFilters((previous) => ({ ...previous, date_to: event.target.value }))}
                                    className="w-full px-3 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:ring-1 focus:ring-[#FF5733] outline-none"
                                />
                            </div>
                            <div className="min-w-0">
                                <Typography className="text-sm font-semibold text-slate-700 mb-1.5">Entry Type</Typography>
                                <Select
                                    options={entryTypeOptions}
                                    value={entryTypeOptions.find((option) => option.value === filters.entry_type)}
                                    onChange={(option) => setFilters((previous) => ({ ...previous, entry_type: option?.value || '' }))}
                                    styles={selectStyles}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isSearchable={false}
                                />
                            </div>
                            <div className="min-w-0">
                                <Typography className="text-sm font-semibold text-slate-700 mb-1.5">Status</Typography>
                                <Select
                                    options={reconciledOptions}
                                    value={reconciledOptions.find((option) => option.value === filters.reconciled)}
                                    onChange={(option) => setFilters((previous) => ({ ...previous, reconciled: option?.value || '' }))}
                                    styles={selectStyles}
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isSearchable={false}
                                />
                            </div>
                            <div className="min-w-0 xl:col-span-1">
                                <Typography className="text-sm font-semibold text-slate-700 mb-1.5">Search</Typography>
                                <div className="flex items-center gap-2 rounded-xl border border-gray-200 bg-white px-3 py-2 focus-within:border-[#FF5733]">
                                    <FontAwesomeIcon icon={faSearch} className="text-slate-400" />
                                    <input
                                        type="text"
                                        value={searchInput}
                                        onChange={(event) => setSearchInput(event.target.value)}
                                        placeholder="Search details or references"
                                        className="w-full border-none bg-transparent text-sm outline-none"
                                    />
                                </div>
                            </div>
                        </div>

                        <div className="mt-4 flex flex-wrap items-center gap-3">
                            <Button
                                type="button"
                                onClick={handleResetFilters}
                                className="bg-slate-100 hover:bg-slate-200 text-slate-700 shadow-none"
                            >
                                Reset Filters
                            </Button>
                            <Typography className="text-sm text-slate-500">
                                {filteredRows.length} of {derivedRows.length} rows shown
                            </Typography>
                            <Typography className="text-sm text-slate-500">
                                Filtered debits {formatMoney(filteredTotalDebits)} • credits {formatMoney(filteredTotalCredits)}
                            </Typography>
                        </div>
                    </CardBody>
                </Card>

                <Card className="border border-[#eadfd8] shadow-sm rounded-xl bg-white">
                    <CardBody className="p-0">
                        {loading ? (
                            <div className="flex justify-center py-16">
                                <Spinner className="h-8 w-8 text-[#FF5733]" />
                            </div>
                        ) : filteredRows.length === 0 ? (
                            <div className="py-16 text-center">
                                <Typography className="text-base font-semibold text-[#2a2e30]">
                                    No statement rows found
                                </Typography>
                                <Typography className="text-sm text-slate-500 mt-2">
                                    Adjust the filters or reset them to see the full statement.
                                </Typography>
                            </div>
                        ) : (
                            <div className="w-full overflow-x-auto">
                                <table className="w-full min-w-[980px] text-left border-collapse">
                                    <thead>
                                        <tr className="bg-slate-50">
                                            <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">Date</th>
                                            <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">Reference</th>
                                            <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200">Details</th>
                                            <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200 text-right">Debit</th>
                                            <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200 text-right">Credit</th>
                                            <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200 text-right">Running Balance</th>
                                            <th className="p-3 text-xs font-semibold uppercase tracking-wide text-slate-500 border-b border-slate-200 text-center">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredRows.map((row) => (
                                            <tr key={row.id} className="border-b border-slate-50 hover:bg-[#fffaf7] transition-colors">
                                                <td className="p-3 border-b border-slate-100 whitespace-nowrap text-sm text-slate-700">
                                                    {formatDate(row.entry_date)}
                                                </td>
                                                <td className="p-3 border-b border-slate-100 whitespace-nowrap text-sm font-mono text-slate-600">
                                                    {row.reference_display}
                                                </td>
                                                <td className="p-3 border-b border-slate-100 text-sm text-slate-700">
                                                    <div className="max-w-[380px]">
                                                        <Typography className="text-sm text-slate-700 break-words">
                                                            {row.details_display}
                                                        </Typography>
                                                        <Typography className="text-[11px] text-slate-400 mt-1">
                                                            {row.entry_type || '-'}
                                                        </Typography>
                                                    </div>
                                                </td>
                                                <td className="p-3 border-b border-slate-100 text-right text-sm font-semibold text-red-600 tabular-nums">
                                                    {row.debit ? formatMoney(row.debit) : '-'}
                                                </td>
                                                <td className="p-3 border-b border-slate-100 text-right text-sm font-semibold text-emerald-600 tabular-nums">
                                                    {row.credit ? formatMoney(row.credit) : '-'}
                                                </td>
                                                <td className="p-3 border-b border-slate-100 text-right text-sm font-bold text-[#2a2e30] tabular-nums">
                                                    {formatMoney(row.running_balance)}
                                                </td>
                                                <td className="p-3 border-b border-slate-100 text-center">
                                                    <Chip
                                                        value={row.status_display}
                                                        size="sm"
                                                        className={`rounded-sm ${
                                                            row.reconciled
                                                                ? 'bg-emerald-50 text-emerald-700'
                                                                : 'bg-slate-100 text-slate-500'
                                                        }`}
                                                    />
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </CardBody>
                </Card>
            </div>
        </div>
    );
};

export default CashbookStatement;
